<script>
    // Narrow the disputes list to one provider / game (869fg1k50). Lives in the
    // URL (providerID, gameID): survives F5 and can be shared. The listing
    // already sends both with `disputed`, so this only edits the query.
    // 869fg2gvw: the boxes list only providers / games that have disputes, with
    // counts, either over every dispute ("все") or over the rows on screen
    // ("эта страница"); the choice is remembered in the browser.
    import { getDisputesSummary } from '@/api/file.js';
    import { groupDisputes, providerLabel } from '@/scripts/disputeSummary.mjs';

    const SCOPE_KEY = 'gup-filter-scope';

    export default {
        data() {
            let scope = 'all';
            try { if (localStorage.getItem(SCOPE_KEY) === 'page') scope = 'page'; } catch { /* private mode */ }
            return { summary: [], scope, providerText: '', gameText: '' };
        },
        computed: {
            providerID() { return this.$route.query.providerID ?? null },
            gameID() { return this.$route.query.gameID ?? null },
            images() { return this.$store.getters.IMAGES },
            // What the boxes offer: the backend summary, or the rows on screen
            // (named from the summary, which knows every provider with disputes).
            source() {
                return this.scope === 'all' ? this.summary : groupDisputes(this.images, this.summary);
            },
            // Lookups go through the full summary so a choice stays valid in both scopes.
            provider() { return this.summary.find((p) => String(p.id) === String(this.providerID)) ?? null },
            sourceProvider() { return this.source.find((p) => String(p.id) === String(this.providerID)) ?? null },
            games() { return (this.sourceProvider ?? this.provider)?.games ?? [] },
            filtered() { return this.providerID != null || this.gameID != null },
        },
        watch: {
            provider: { handler(p) { this.providerText = p?.name ?? '' }, immediate: true },
            gameID: { handler() { this.syncGame() }, immediate: true },
            summary() { this.syncGame() },
            // A verdict shrinks the list: refresh the counts with it.
            async '$store.getters.IMAGE_COUNT'() { this.summary = (await getDisputesSummary()) ?? []; },
            scope(v) {
                try { localStorage.setItem(SCOPE_KEY, v); } catch { /* private mode */ }
            },
        },
        async created() {
            this.summary = (await getDisputesSummary()) ?? [];
        },
        methods: {
            providerLabel,
            syncGame() {
                const all = this.provider?.games ?? [];
                this.gameText = all.find((g) => String(g.id) === String(this.gameID))?.name ?? '';
            },
            go(providerID, gameID) {
                const query = { ...this.$route.query, providerID: providerID ?? undefined, gameID: gameID ?? undefined };
                delete query.page;
                this.$router.replace({ query });
            },
            pickProvider() {
                const p = this.summary.find((x) => x.name === this.providerText.trim());
                if (p) this.go(p.id, null);
                else if (this.providerText.trim() === '') this.go(null, null);
            },
            pickGame() {
                const g = (this.provider?.games ?? []).find((x) => x.name === this.gameText.trim());
                if (g) this.go(this.providerID, g.id);
                else if (this.gameText.trim() === '') this.go(this.providerID, null);
            },
            reset() { this.go(null, null) },
        },
    };
</script>

<template>
    <div class="dfilter">
        <span class="dfilter_label">Показать</span>
        <div class="dfilter_scope" role="group" aria-label="Откуда брать провайдеров и игры">
            <button
                type="button"
                :class="{ on: scope === 'page' }"
                :aria-pressed="scope === 'page'"
                title="Только провайдеры и игры из строк на этой странице"
                @click="scope = 'page'"
            >эта страница</button>
            <button
                type="button"
                :class="{ on: scope === 'all' }"
                :aria-pressed="scope === 'all'"
                title="Все провайдеры и игры, где есть спорные"
                @click="scope = 'all'"
            >все</button>
        </div>
        <label class="dfilter_field">Провайдер
            <input
                v-model="providerText"
                list="dispute-providers"
                placeholder="Все провайдеры"
                @change="pickProvider"
            >
            <datalist id="dispute-providers">
                <option v-for="p in source" :key="p.id" :value="p.name" :label="providerLabel(p)"></option>
            </datalist>
        </label>
        <label class="dfilter_field">Игра
            <input
                v-model="gameText"
                list="dispute-games"
                :placeholder="provider ? 'Все игры' : 'Сначала провайдер'"
                :disabled="!provider"
                @change="pickGame"
            >
            <datalist id="dispute-games">
                <option v-for="g in games" :key="g.id" :value="g.name" :label="`${g.count}`"></option>
            </datalist>
        </label>
        <button v-if="filtered" class="dfilter_reset" aria-label="Сбросить фильтр" @click="reset">× Сбросить</button>
    </div>
</template>

<style scoped>
    .dfilter {
        display: flex; align-items: center; flex-wrap: wrap; gap: 14px;
        padding: 14px 20px;
        border-bottom: 1px solid var(--background-hover);
    }
    .dfilter_label {
        font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase;
        color: var(--text-color-secondary); font-weight: bold;
    }
    .dfilter_scope { display: flex; border: 1px solid var(--border-color); border-radius: 5px; overflow: hidden; }
    .dfilter_scope button {
        height: 36px; padding: 0 12px; border: 0;
        background: var(--background-color); color: var(--text-color);
        font: inherit; font-size: 13px; cursor: pointer;
    }
    .dfilter_scope button + button { border-left: 1px solid var(--border-color); }
    .dfilter_scope button.on { background: var(--main-color); color: #ffffff; font-weight: bold; }
    .dfilter_field { display: flex; align-items: center; gap: 8px; font-size: 13px; }
    .dfilter_field input {
        height: 36px; width: 220px; box-sizing: border-box;
        border: 1px solid var(--border-color); border-radius: 5px; padding: 0 10px;
        font: inherit; font-size: 13px;
        background: var(--background-color); color: var(--text-color);
    }
    .dfilter_field input:disabled { opacity: 0.55; }
    .dfilter_reset {
        height: 36px; padding: 0 12px;
        border: 1px solid var(--border-color); border-radius: 5px;
        background: var(--background-color); color: var(--text-color);
        font: inherit; font-size: 13px; cursor: pointer;
    }
    .dfilter_reset:hover { background: var(--background-hover); }
</style>
